import * as React from 'react';

import {
    useTranslation,
    useForm, G, useSearch,
} from '@components';
import {
    IMachinery, ISecurity, IWork, ModuleManager, IMachineryType
} from '@models';
import './RestrictMachinery.scss';

interface IProps {
    security: ISecurity;
    moduleManager: ModuleManager;
    work: IWork;
    selectedMachineryId?: number;
    data: any[];
    restrictMachinery: Function;
    handleToast: Function;
    onClose: Function;
    machineryTypes: IMachineryType[];
    editRestriction: Function;
}

interface IResult {
    id: number,
    machineryType: number
    machineryTypeName: string
    name: string
    licenseNumber: string
    code: string
    contractorName: string
    applicantName: string
    applicantEmail: string
    date: string
    reason: string
    remarks: string
    isActive: boolean
    deactivatedAt?: string
    workId: number
}

export function RestrictMachinery(props: IProps) {
    const { t } = useTranslation();
    const [ machinery, setMachinery ] = React.useState<IMachinery & { isRestrictedMachinery?: boolean }>(props.data.find(x => x.id === props.selectedMachineryId));
    const [ disabled, setDisabled ] = React.useState(false);
    const [ canModify, setCanModify ] = React.useState<boolean>(false);

    const restriction = useSearch<IResult>({
                workId: props.work.id,
                search: 'machineries/restricted',
                filters: {
                    workId: props.work.id,
                    machineryCode: (machinery?.code && machinery.code.trim() !== '') ? machinery.code : undefined,
                    machineryLicense: machinery?.licenseNumber && machinery.licenseNumber.trim() !== '' ? machinery.licenseNumber : undefined,
                },
            });

    const calculateCanModify = () => {
        if (!machinery.isRestrictedMachinery) {
            setCanModify(true);
        }
        if (props.security.isGestor()) {
            setCanModify(true);
        }
    }

    const initialize = () => {
        const restrictionData = restriction.value[0];
        if (restrictionData) {
            setRestrictionId(restrictionData.id);
            setApplicantName(restrictionData.applicantName);
            setApplicantEmail(restrictionData.applicantEmail);
            setDate(restrictionData.date)
            setReason(restrictionData.reason);
            setRemarks(restrictionData.remarks ?? '');
        }
    }

    React.useEffect(() => {
        calculateCanModify();
        if(machinery?.isRestrictedMachinery === true) {
            restriction.doSearch();
        }
    }, []);

    React.useEffect(() => {
        if (machinery?.isRestrictedMachinery === true) {
            initialize();
        }
    }, [restriction.value]);

    const [ restrictionId, setRestrictionId ] = React.useState<number | undefined>(undefined);
    const [ applicantName, setApplicantName ] = React.useState<string>(props.security.user?.userName ?? '');
    const [ applicantEmail, setApplicantEmail ] = React.useState<string>(props.security.user?.email ?? '');
    const [ date, setDate ] = React.useState<string>(new Date().toISOString().split('T')[0]);
    const [ reason, setReason ] = React.useState<string>('');
    const [ remarks, setRemarks ] = React.useState<string>('');

    const handleSave = async () => {
        const resp = await props.restrictMachinery(
            props.work.id,
            machinery?.id,
            machinery?.machineryTypeId,
            machinery?.name ?? '',
            machinery?.prefixLicenseNumber ?? '',
            machinery?.licenseNumber ?? '',
            machinery?.suffixLicenseNumber ?? '',
            machinery?.code ?? '',
            machinery?.contractorId,
            applicantName,
            applicantEmail,
            reason,
            remarks
        );

        if (resp.isError === false && typeof resp.value == 'number') {
            props.handleToast(t('Machinery restricted successfully'), true);
            props.onClose();
        } else {
            props.handleToast(t('Error restricting machinery'), false);
            props.onClose();
        }
    }


    const handleSubmit = () => {
        if (machinery?.isRestrictedMachinery) {
            editRestriction();
        } else {
            handleSave();
        }
    }

    const editRestriction = async () => {
        setDisabled(true);
        const response = await props.editRestriction(restrictionId, applicantName, applicantEmail, reason, remarks)

        if(response.data.editMachineryRestriction.isError || response.data.editMachineryRestriction.value == 0) {
            props.handleToast(t('Error while editing restriction'), false);
            props.onClose();
        }else {
            props.handleToast(t('Restriction has been edited successfully'), true);
            props.onClose();
        }
    }


    return (
        <div className='restrict-machinery-dialog'>
            <G label={t('Machinery type')}>
                <input
                    disabled={true}
                    className='c'
                    type='text'
                    value={props.machineryTypes.find(x => x.id === machinery?.machineryTypeId)?.name}
                />
            </G>
            <G label={t('Name')}>
                <input
                    disabled={true}
                    className='c'
                    type='text'
                    value={machinery?.name}
                />
            </G>
            <G label={t('License number')}>
                <input
                    disabled={true}
                    className='c'
                    type='text'
                    value={`${machinery?.prefixLicenseNumber}${machinery?.licenseNumber}${machinery?.suffixLicenseNumber}` }
                />
            </G>
            <G label={t('machinery.codes')}>
                <input
                    disabled={true}
                    className='c'
                    type='text'
                    value={machinery?.code}
                />
            </G>
            <G label={t('Contractor')}>
                <input
                    disabled={true}
                    className='c'
                    type='text'
                    value={machinery?.contractorName}
                />
            </G>
            <G label={t('Applicant')}>
                <input
                    disabled={!canModify}
                    className='c'
                    type='text'
                    value={applicantName}
                    onChange={(e) => setApplicantName(e.target.value)}
                />
            </G>
            <G label={t('Applicant email')}>
                <input
                    disabled={!canModify}
                    className='c'
                    type='email'
                    value={applicantEmail}
                    onChange={(e) => setApplicantEmail(e.target.value)}
                />
            </G>
            <G label={t('Date')}>
                <input
                    disabled={restrictionId !== undefined}
                    className='c'
                    type='date'
                    value={date.split('T')[0]}
                    onChange={(e) => setDate(e.target.value)}
                />
            </G>
            <G label={t('Motive')} className='restriction-reason'>
                <textarea
                    disabled={!canModify}
                    className='c'
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                />
            </G>
            <G label={t('Remarks')} className='restriction-remarks'>
                <textarea
                    disabled={!canModify}
                    className='c'
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                />
            </G>
            <div className='c' />
            <div className='r'>
                <span className='e' />

                { canModify && <button
                    className='primary'
                    disabled={disabled}
                    onClick={handleSubmit}>
                    {t('Save')}
                </button>}
            </div>
        </div>
    );
}
